/* eslint-disable no-debugger */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-console */
/* eslint-disable quotes */
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import PaymentsActions from "../../../redux/reducers/payments.reducer";
import MoldalActions from "../../../redux/reducers/modal.reducer";
import IssuerPaymentModel from "../../../lib/models/issuerPayment.model";
import theme from "../../../styles/LightTheme";

/**
 * Hook para manejar acciones de pagos
 * Centraliza toda la lógica de negocio: pagar, descargar, aprobar
 */
export const usePaymentActions = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { state: stateFromModel } = IssuerPaymentModel;

  /**
   * Maneja el clic en el botón "Pagar"
   * Recibe paymentData del PaymentCard con toda la info necesaria
   */
  const handleClickPay = useCallback(
    (
      campaignId,
      issuerPayment,
      isOverdue = false,
      overdueTotal = 0,
      paymentData = {}
    ) => {
      const { amount, paymentType } = paymentData;

      // Calcular el optionValue según el tipo de pago
      // Positivo para pago total, negativo para custom
      const optionValue =
        paymentType === "total" ? issuerPayment.id : issuerPayment.id * -1;

      // Detectar si es pago parcial en mora
      // Condiciones: custom + overdue + amount < overdueTotal
      const isPartialOverdue =
        paymentType === "custom" &&
        isOverdue &&
        amount > 0 &&
        amount < overdueTotal;

      dispatch(
        PaymentsActions.setCampaignSelected(
          campaignId,
          issuerPayment,
          optionValue,
          isOverdue,
          isOverdue ? overdueTotal : amount,
          isPartialOverdue
        )
      );

      if (paymentType === "custom") {
        dispatch(PaymentsActions.setOtherAmount(amount));

        if (isPartialOverdue) {
          sessionStorage.setItem("paymentType", "partial_overdue_payment");
        }
      }

      const goToConfirm = [
        stateFromModel.approved_by_timeout,
        stateFromModel.issuer_approved,
        stateFromModel.rejected,
        stateFromModel.late_payment
      ].includes(issuerPayment.state);

      // Para pagos NO aprobados (is_approved = false), NO navegamos aquí
      // La navegación la hace el epic approvePayment después de aprobar
      if (!issuerPayment.is_approved) {
        return;
      }

      if (goToConfirm) {
        return dispatch(push("/confirm-pay-amount"));
      }
    },
    [dispatch, stateFromModel]
  );

  /**
   * Configura la campaña seleccionada sin navegar
   * Usado antes de aprobar un pago pending_approve
   */
  const setupCampaignForPayment = useCallback(
    (
      campaignId,
      issuerPayment,
      isOverdue = false,
      overdueTotal = 0,
      paymentData = {}
    ) => {
      const { amount, paymentType } = paymentData;

      const optionValue =
        paymentType === "total" ? issuerPayment.id : issuerPayment.id * -1;

      const isPartialOverdue =
        paymentType === "custom" &&
        isOverdue &&
        amount > 0 &&
        amount < overdueTotal;

      const setCampaignAction = PaymentsActions.setCampaignSelected(
        campaignId,
        issuerPayment,
        optionValue,
        isOverdue,
        isOverdue ? overdueTotal : amount,
        isPartialOverdue
      );

      dispatch(setCampaignAction);

      if (paymentType === "custom") {
        dispatch(PaymentsActions.setOtherAmount(amount));

        if (isPartialOverdue) {
          sessionStorage.setItem("paymentType", "partial_overdue_payment");
        }
      }
    },
    [dispatch]
  );

  /**
   * Descarga archivo Excel de derechos patrimoniales
   * Recibe datos de campaña desde PaymentCard
   */
  const handleDownloadExcel = useCallback(
    (campaignData) => {
      const { campaignId, userId, userEmail, issuerPaymentId, isOverdue } = campaignData;
      const modalGeneral = {
        clearBlur: true,
        css: {
          backgroundColor: theme.transparentMartinique
        }
      };

      dispatch(
        MoldalActions.setConditionalModalState(
          true,
          "ModalLoadingCertificate",
          {
            ...modalGeneral,
            description: t("searchInvestDetail.download")
          }
        )
      );

      dispatch(
        PaymentsActions.downloadFileExcel({
          isLoading: false,
          campaignId,
          userId,
          userEmail,
          issuerPaymentID: issuerPaymentId,
          isOverdue
        })
      );
    },
    [dispatch, t]
  );
  
  /**
   * Aprueba un issuer_payment usando el epic de Redux
   * Este método centraliza toda la lógica de aprobación:
   * 1. Configura la campaña seleccionada en Redux
   * 2. Despacha la acción approveIssuerPayment al epic
   * 3. El epic llama al ACC (PATCH /api/v2/payments/:id/approve)
   * 4. Actualiza is_approved=true en la BD
   */
  const handleApprovePayment = useCallback(
    (
      issuerPaymentId,
      campaignId,
      issuerPayment,
      isOverdue = false,
      overdueTotal = 0,
      paymentData = {}
    ) => {
      setupCampaignForPayment(
        campaignId,
        issuerPayment,
        isOverdue,
        overdueTotal,
        paymentData
      );

      const action = PaymentsActions.approveIssuerPayment({
        issuerPaymentId
      });
      dispatch(action);
    },
    [dispatch, setupCampaignForPayment]
  );

  /**
   * Navega al historial de pagos
   */
  const handleClickLink = useCallback(() => {
    dispatch(push("/payments"));
  }, [dispatch]);

  /**
   * Navega a la página principal
   */
  const handleBack = useCallback(() => {
    dispatch(push("/"));
  }, [dispatch]);

  return {
    handleClickPay,
    handleDownloadExcel,
    handleApprovePayment,
    handleClickLink,
    handleBack,
    setupCampaignForPayment
  };
};
